import {jsonToCSV} from 'react-papaparse'
import {
    InputFileAcceptMimes,
    InputFileAcceptTypes,
    ProductTechnicalPropertyTypeIds,
    WarrantyAndReturnMessageTypes
} from "../../constants/enums";
import accepts from "attr-accept";
import {v4 as UUIDv4} from 'uuid';
import {format, normalize} from 'phone-formatter';
import html2canvas from "html2canvas";
import EnvService from "../env";
import PartnerIndustryRules from '../partner-industry-rules';
import $ from 'jquery';
import DefaultColorTheme from "../../../ui/theme/default-color-theme";
import {UnparseConfig} from "papaparse";
import DateUtils, {DateUtilsTypes} from "../date-utils";
import {EncryptionUtils} from "./encryption-utils";
import i18next from "i18next";
import {ColorInputColorVisualizer} from "../../../ui/components/text-fields/color-field";
import {Yup} from "../../../ui/components/form";
import ValidateMessages from "../../models/static/texts/vallidate-messages";

declare global {
    interface Window {
        FlutterBridge?: { postMessage: (msg: string) => void };
        webkit?: { messageHandlers?: Record<string, { postMessage: (msg: any) => void }> };
    }
}

/**
 * Determines if two objects are equal
 * @param object1 {any}
 * @param object2 {any}
 * @return {boolean}
 */
export const deepEqual = (object1: any, object2: any): boolean => {
    // check if the first one is an array
    if (Array.isArray(object1)) {
        if (!Array.isArray(object2) || object1.length !== object2.length) return false;
        for (let i = 0; i < object1.length; i++) {
            if (!deepEqual(object1[i], object2[i])) return false;
        }
        return true;
    }
    // check if the first one is an object
    if (typeof object1 === 'object' && object1 !== null && object2 !== null) {
        if (!(typeof object2 === 'object')) return false;
        const keys = Object.keys(object1);
        if (keys.length !== Object.keys(object2).length) return false;
        for (const key in object1) {
            if (!deepEqual(object1[key], object2[key])) return false;
        }
        return true;
    }
    // not array and not object, therefore must be primitive
    return object1 === object2;
}

/**
 *  Deep copy an acyclic *basic* Javascript object.  This only handles basic
 * scalars (strings, numbers, booleans) and arbitrarily deep arrays and objects
 * containing these.  This does *not* handle instances of other classes.
 * @param obj {any}
 */
export const deepCopy = <T = any>(obj: T): T => {
    let ret: any, key;
    let marker = '__deepCopy';

    // @ts-ignore
    if (obj && obj[marker])
        throw (new Error('attempted deep copy of cyclic object'));

    // @ts-ignore
    if (obj && obj.constructor === Object) {
        ret = {};
        // @ts-ignore
        obj[marker] = true;

        for (key in obj) {
            if (key === marker)
                continue;

            // @ts-ignore
            ret[key] = deepCopy(obj[key]);
        }

        // @ts-ignore
        delete (obj[marker]);
        return (ret);
    }

    // @ts-ignore
    if (obj && obj.constructor === Array) {
        ret = [];
        // @ts-ignore
        obj[marker] = true;

        // @ts-ignore
        for (key = 0; key < obj.length; key++)
            ret.push(deepCopy(obj[key]));

        // @ts-ignore
        delete (obj[marker]);
        return (ret);
    }
    // It must be a primitive type -- just return it.
    return (obj);
}

/**
 * Transforms a string that is parsable to a number into a formatted money string.
 * @param amount {string | number}
 * @param decimalCount {number}
 * @param decimal {string} the identifier used for decimal separation
 * @param thousands {string} the identifier used for thousands separation
 */
export const formatMoney = (amount: any, decimalCount = 2, decimal = ".", thousands = ",") => {
    try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";
        amount = Math.abs(Number(amount) || 0).toFixed(decimalCount);
        let i: any = parseInt(amount).toString();
        let j = (i.length > 3) ? i.length % 3 : 0;

        return negativeSign + "$" + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) + (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : "");
    } catch (e) {
        console.log(e)
    }
};

/**
 * Gets the offset of an element based on the parent id
 * @param element {HTMLElement | Element | null}
 * @param parentId {string}
 */
export const getOffsetTop = (element: HTMLElement | Element | null, parentId: string) => {
    let offsetTop = 0;
    while (element && element.id !== parentId) {
        offsetTop += (element as HTMLElement).offsetTop;
        element = (element as HTMLElement).offsetParent;
    }
    return offsetTop;
}

/**
 * Determines if a given target is within the vicinity of a central point based on the specified percentage
 * @param target {number}
 * @param center {number}
 * @param percent {number}
 * @param threshHold {number}
 */
export const withinVicinity = (target: number, center: number, percent: number = 0, threshHold: number = 0): boolean => {
    const difference = Math.abs(center - target);
    return (Math.abs(center) + ((percent / 100) * Math.abs(center)) - threshHold) >= difference;
}

/**
 * Exports a file into csv by the given headers, items and the file title
 * @param items {any}
 * @param fileTitle {string}
 * @param options {UnparseConfig}
 */
export const exportCSVFile = (items: any, fileTitle: string, options: UnparseConfig = {}): void => {
    const csv = jsonToCSV(items, options ?? {});
    const exportedFileName = fileTitle ? fileTitle.toLowerCase().endsWith('.csv') ? fileTitle : `${fileTitle}.csv` : 'export.csv';
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    if (navigator.msSaveBlob) { // IE 10+
        navigator.msSaveBlob(blob, exportedFileName);
    } else {
        const link = document.createElement("a");
        if (link.download !== undefined) {
            link.href = URL.createObjectURL(blob);
            link.download = exportedFileName;
            link.style.visibility = 'hidden';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}


/**
 * Extracts the base64 content of a blob file
 * @param file {Blob | File}
 */
export const readFileAsDataUrl = (file: File | Blob): PromiseLike<string | ArrayBuffer | null> => new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

/**
 * Extracts the text content of a blob file.
 * Valid UTF-8 is kept. Other bytes, such as Excel Windows-1252 CSV, are decoded as Windows-1252.
 * @param file {Blob | File}
 */
export const readFileAsText = (file: File | Blob): PromiseLike<string | ArrayBuffer | null> => new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
        const result = reader.result;
        if (!(result instanceof ArrayBuffer)) {
            resolve(result);
            return;
        }
        resolve(Utils.decodeImportedText(new Uint8Array(result)));
    };
    reader.onerror = error => reject(error);
});

/**
 * fills the card number empty spaces with *s
 * @param lastFour
 */
export const getCardNumber = (lastFour: string) => {
    return `**** - **** - ${lastFour}`;
}

/**
 * fills the card month empty spaces with *s
 * @param month
 */
export const getCardMonth = (month: string) => {
    if (!month) return '**/**';
    if (month?.trim()?.length === 1) {
        return `0${month.trim()}/**`;
    }
    return `${month.trim()}/**`;
}

/**
 * Opens the provided link into a new tab of the browser
 * @param link {string} the link to be opened in a new tab
 */
export const openLinkInNewTab = (link: string) => {
    if (!link?.length) return;
    const newTab = document.createElement('a');
    newTab.href = link;
    newTab.target = '_blank';
    newTab.rel = 'noopenner';
    document.body.appendChild(newTab);
    newTab.click();
    document.body.removeChild(newTab);
}

/**
 * Opens the provided link into a new tab of the browser
 * @param blob {Blob} the blob to download
 * @param fileName {string} the name of the file to download
 */
export const downloadFile = (blob: Blob, fileName = null) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName ?? 'unknown';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
}

/**
 * Creates a Unique Identifier in form of a string
 */
export const createUUId = (reactKey = false) => {
    const uuid = UUIDv4();
    if (!reactKey) return uuid;
    return `_${uuid}`
}

export const ReadingFileAs = {
    binaryString: 'binaryString',
    dataUrl: 'dataUrl',
    text: 'text',
    arrayBuffer: 'arrayBuffer',
}

/**
 * Reads the given file based on the provided method of reading.
 * @param {File | Blob} file
 * @param {string} as
 */
export const readFile = (file: File | Blob, as: string): PromiseLike<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        switch (as) {
            case ReadingFileAs.text:
                reader.readAsText(file);
                break;
            case ReadingFileAs.dataUrl:
                reader.readAsDataURL(file);
                break;
            case ReadingFileAs.arrayBuffer:
                reader.readAsArrayBuffer(file);
                break;
            case ReadingFileAs.binaryString:
                reader.readAsBinaryString(file);
                break;
        }
        reader.onload = () => {
            resolve(reader.result)
        };
        reader.onerror = error => reject(error);
    });
}

/**
 * Given an html text, removes all the tags and only shows the texts between them.
 * @param text {string}
 * @param limit {number}
 * @return {string}
 */
export const htmlToString = (text: string, limit: number = 100): string => {
    if (!text)
        return '';
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = text;
    let cleanText = tempDivElement.textContent ?? tempDivElement.innerText ?? "";
    if (cleanText.length > limit) {
        cleanText = cleanText.substring(0, limit).concat('...');
    }
    return cleanText;
}

/**
 * For each of the files, sends them to the server api as a new message one by one.
 * @param images {File[]}
 * @param createObject {Function}
 */
export const sendChatFiles = (images: File[], createObject: Function) => {
    if (!images?.length) return;
    const forSend = [];
    for (let index = 0; index < images.length; ++index) {
        let type;
        const item: any = images[index]
        if (accepts(item, InputFileAcceptMimes.images)) {
            type = WarrantyAndReturnMessageTypes.image
        } else if (accepts(item, InputFileAcceptTypes.pdf)) {
            type = WarrantyAndReturnMessageTypes.pdf
        } else if (accepts(item, [InputFileAcceptTypes.zip, InputFileAcceptTypes.rar])) {
            type = WarrantyAndReturnMessageTypes.zip
        } else if (accepts(item, InputFileAcceptMimes.videos)) {
            type = WarrantyAndReturnMessageTypes.video
        }
        // @ts-ignore
        forSend.push(createObject(item, type, index))
    }
    return forSend;
}

/**
 * Converts a data url to an array buffer that is to be used in fileContent of upload apis.
 * @param {string} dataUrl the data url that is used in img src'
 */
export const dataUrlToArrayBuffer = async (dataUrl: string): Promise<string | ArrayBuffer | null> => {
    try {
        const res = await fetch(dataUrl)
        return await readFile(await res.blob(), ReadingFileAs.binaryString);
    } catch (e) {
        return null;
    }
}

/**
 * Formats the given phone number.
 *
 * @param number the phone number to format.
 * @param options the area code of the number as its suffix.
 */
export const formatPhoneNumber = (number: string, options?: {
    normalize?: boolean,
    pattern?: string,
    withoutAreaCode?: boolean,
    areaCode?: string
}) => {
    const imputedOptions = {
        normalize: true,
        pattern: '+N (NNN) NNN-NNNN',
        withoutAreaCode: false,
        areaCode: '1',
        ...(options ?? {}),
    }
    if (!number) {
        return '--';
    }
    if (imputedOptions.normalize) {
        number = normalize(number);
    }
    if (!imputedOptions.withoutAreaCode && imputedOptions.areaCode) {
        number = imputedOptions.areaCode.toString() + number;
    }
    return format(number, imputedOptions.pattern, {normalize: true});
}

/**
 * Converts the given hex value to its rgb counterpart.
 * @param {string} hex
 * @return {Array<number> | null}
 */
export const hexToRGB = (hex: string): Array<number> | null => {
    if (hex?.length !== 7) {
        return null;
    }
    hex = hex.slice(1);
    const aRgbHex = hex.match(/.{1,2}/g);
    if (!aRgbHex)
        return null;

    return [
        parseInt(aRgbHex[0], 16),
        parseInt(aRgbHex[1], 16),
        parseInt(aRgbHex[2], 16)
    ];
}

type ObjectKey = string | number | symbol;

/**
 * Takes an Array<V>, and a grouping function and returns a Map of the array grouped by the grouping function.
 *
 * @param items     An array of type TItem.
 * @param keyGetter A Function that takes the Array type TItem as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of TItem.
 *
 * @returns Map of the array grouped by the grouping function.
 */
export const groupBy = <K extends ObjectKey, TItem extends Record<K, ObjectKey>>(
    items: TItem[],
    keyGetter: (input: TItem) => K
): Record<ObjectKey, TItem[]> => {
    return items.reduce((result, item) => ({
        ...result,
        [keyGetter(item)]: [
            ...(result[keyGetter(item)] || []),
            item
        ],
    }), {} as Record<ObjectKey, TItem[]>);
}

/**
 * Prevents the propagation of the given event and then proceeds to call the given callback fn.
 * @param {Event} e
 * @param {Function} fn
 */
export const preventEventPropagation = (e: Event, fn: Function | undefined = undefined) => {
    e.stopPropagation();
    e.preventDefault();
    if (typeof fn === 'function') {
        fn();
    }
}

/**
 * Fetches the first scrolling ancestor of the given element.
 *
 * @param {HTMLElement} element
 * @param {boolean} includeHidden whether to also include elements with "overflow: hidden" or not. defaults to false.
 */
export const getFirstScrollingAncestor = (element: HTMLElement, includeHidden: boolean = false): HTMLElement => {
    let style = getComputedStyle(element);
    let excludeStaticParent = style.position === "absolute";
    let overflowRegex = includeHidden ? /(auto|scroll|hidden)/ : /(auto|scroll)/;

    if (style.position === "fixed") return document.body;
    for (let parent: HTMLElement | null = element; (parent = parent.parentElement);) {
        if (!parent)
            return document.body;

        style = getComputedStyle(parent);
        if (excludeStaticParent && style.position === "static")
            continue;

        if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX))
            return parent;
    }

    return document.body;
}

//              ########################### COMPARATORS ###################################

/**
 * Compares two numbers
 * @param a {number}
 * @param b {number}
 */
export const numComparator = (a: number, b: number): number => {
    if (a === b) return 0;
    if (a < b) return -1;
    return 1
}

/**
 * Compares two dates by converting them to objects and then comparing them
 * @param a
 * @param b
 */
export const dateComparator = (a: DateUtilsTypes.FormatObject, b: DateUtilsTypes.FormatObject): number => {
    const _dayjsComparator = (a: DateUtilsTypes.Instance, b: DateUtilsTypes.Instance) => {
        if (a.isSame(b, 'ms')) return 0;
        if (a.isAfter(b, 'ms')) return 1;
        return -1;
    }
    return _dayjsComparator(DateUtils.server(a), DateUtils.server(b));
}


const collatorCache = new Map<string, Intl.Collator>();

/**
 * Fetches a string collator for the given locale
 * @param locale
 */
const getStringCollator = (locale?: string) => {
    const key = locale ?? 'default';
    if (!collatorCache.has(key)) {
        collatorCache.set(
            key,
            new Intl.Collator(locale, {
                sensitivity: 'base',
                numeric: true,
                ignorePunctuation: true,
            })
        );
    }
    return collatorCache.get(key)!;
}

/**
 * Compares two strings.
 * @param a {string}
 * @param b {string}
 */
export const stringComparator = (a: string, b: string): number => {
    const language = i18next.resolvedLanguage ?? i18next.language;
    return getStringCollator(language).compare(a ?? "", b ?? "");
}

/**
 * Compares two Booleans
 * @param a {boolean}
 * @param b {boolean}
 */
export const booleanComparator = (a: boolean, b: boolean): number => {
    if (a === b) return 0;
    if (a < b) return -1;
    return 1;
}


/**
 * Options for reading a file.
 */
export enum FileReadingOptions {
    binaryString = 'binaryString',
    dataUrl = 'dataUrl',
    text = 'text',
    arrayBuffer = 'arrayBuffer',
    json = 'json',
}

/**
 * Different possible entries found in the [window.navigator.platform] value.
 */
export enum OperatingSystems {
    mac = 'Mac',
    ios = 'IOS',
    windows = 'Windows',
    android = 'Android',
    linux = 'Linux',
}

class Utils {

    /**
     * Compares two dates by converting them to objects and then comparing them
     * @param a
     * @param b
     * @param unit
     */
    static dateComparator(a: DateUtilsTypes.FormatObject, b: DateUtilsTypes.FormatObject, unit: DateUtilsTypes.OpUnitType = 'ms'): number {
        const _dayjsComparator = (a: DateUtilsTypes.Instance, b: DateUtilsTypes.Instance) => {
            if (a.isSame(b, unit)) return 0;
            if (a.isAfter(b, unit)) return 1;
            return -1;
        }
        return _dayjsComparator(DateUtils.server(a), DateUtils.server(b));
    }


    /**
     * Determines if two objects are equal.
     *
     * @param object1       the first object
     * @param object2       the second object
     * @return              true if the objects are equal, false otherwise
     */
    static deepEqual(object1: any, object2: any): boolean {
        // check if the first one is an array
        if (Array.isArray(object1)) {
            if (!Array.isArray(object2) || object1.length !== object2.length) return false;
            for (let i = 0; i < object1.length; i++) {
                if (!this.deepEqual(object1[i], object2[i])) return false;
            }
            return true;
        }
        // check if the first one is an object
        if (typeof object1 === 'object' && object1 !== null && object2 !== null) {
            if (!(typeof object2 === 'object')) return false;
            const keys = Object.keys(object1);
            if (keys.length !== Object.keys(object2).length) return false;
            for (const key in object1) {
                if (!this.deepEqual(object1[key], object2[key])) return false;
            }
            return true;
        }
        // not array and not object, therefore must be primitive
        return object1 === object2;
    }

    /**
     * Deep copy an acyclic *basic* Javascript object.  T
     *
     * * this method only handles basic scalars (strings, numbers, booleans) and arbitrarily deep arrays and objects
     * containing these.
     * * This method does *not* handle instances of other classes.
     * @template T      the type of the object
     * @param obj       the object to be deep copied
     * @return          the deep copied object
     */
    static deepCopy<T = any>(obj: T): T {
        let ret: any, key;
        let marker = '__deepCopy';

        // @ts-ignore
        if (obj && obj[marker])
            throw (new Error('attempted deep copy of cyclic object'));

        if (obj && obj.constructor === Object) {
            ret = {};
            // @ts-ignore
            obj[marker] = true;

            for (key in obj) {
                if (key === marker)
                    continue;

                // @ts-ignore
                ret[key] = this.deepCopy(obj[key]);
            }

            // @ts-ignore
            delete (obj[marker]);
            return (ret);
        }

        // @ts-ignore
        if (obj && obj.constructor === Array) {
            ret = [];
            // @ts-ignore
            obj[marker] = true;

            // @ts-ignore
            for (key = 0; key < obj.length; key++)
                ret.push(this.deepCopy(obj[key]));

            // @ts-ignore
            delete (obj[marker]);
            return (ret);
        }
        // It must be a primitive type -- just return it.
        return (obj);
    }

    /**
     * Performs a deep merge of objects and returns new object. Does not modify
     * objects (immutable) and merges arrays via concatenation.
     *
     * @param objects           the objects to be merged
     * @returns                 the merged object
     */
    static deepMerge(...objects: Record<string, any>[]): Record<string, any> {
        const isObject = (obj: any) => obj && typeof obj === 'object';

        return objects.reduce((prev: any, obj: any) => {
            for (const key of Object.keys(obj)) {
                const pVal = prev[key];
                const oVal = obj[key];

                if (Array.isArray(pVal) && Array.isArray(oVal)) {
                    prev[key] = [...new Set([...oVal, ...pVal])];
                } else if (isObject(pVal) && isObject(oVal)) {
                    prev[key] = this.deepMerge(pVal, oVal);
                } else {
                    prev[key] = oVal;
                }
            }

            return prev;
        }, {});
    }

    /**
     * Given an object, will flatten it and return all of its values as a single.
     *
     * if append, then for each of the values of the object, appends it to their values as a string
     * @param object {any}
     * @param append {string | null}
     */
    static flattenObjectAndReturnAsAList(object: any, append: string | null = null): string[] | any[] {
        const all = this.flattenObject(object);
        const res: any[] = [];
        for (const [key, value] of Object.entries(all)) {
            if (key) res.push(value);
        }
        if (append && append.length) return res.map(e => `${e}${append}`);
        return res;
    };

    /**
     * Fetches the color name associated with the icon of the vendors' warranty rate.
     * @param {number} warrantyRate
     * @return {string}
     */
    static getWarrantyRateIconColor(warrantyRate: number = 0): string {
        if (warrantyRate < 0.26)
            return 'good';
        if (warrantyRate < 0.51)
            return 'normal'
        if (warrantyRate < 0.76)
            return 'not-good'
        return 'not-acceptable';
    }

    /**
     * Awaits for the specified time in milliseconds.
     * @param {number} milliseconds
     * @return {Promise<void>}
     */
    static async wait(milliseconds: number): Promise<void> {
        await new Promise(r => setTimeout(r, milliseconds));
    }

    /**
     * Sets the application's title and description from given values.
     *
     * @param {string?} title
     * @param {string?} description
     */
    static setAppInfo({
                          title,
                          description
                      } = {title: EnvService.websiteName, description: EnvService.websiteDescription}) {
        if (title) {
            document.title = title;
        }
        if (description) {
            $('meta[name="description"]').attr("content", description);
        }
    }

    /**
     * Creates a Unique Identifier in form of a string
     * @return {string}
     */
    static createUUID(): string {
        const uuid = UUIDv4();
        return `ID-${uuid}`
    }

    /**
     * Fetches the aspect ratio between the given width and height.
     * @param {number} width
     * @param {number} height
     * @return {[number, number]}
     */
    static getAspectRatio(width: number, height: number): [number, number] {
        const gcd = (a: number, b: number): number => a ? gcd(b % a, a) : b;
        const widthToHeightAspectRaw = Number((width / height).toFixed(2));
        const raisedWidth = Math.round(widthToHeightAspectRaw * 100);
        const raisedHeight = 100;
        const _gcd = gcd(raisedWidth, raisedHeight);
        return [raisedWidth / _gcd, raisedHeight / _gcd];
    }

    /**
     * Excludes null or undefined from the values of the object.
     * @param {Object} obj
     * @param {boolean} excludeNull
     * @param {boolean} excludeUndefined
     * @return {Object}
     */
    static excludeNullOrUndefined<T extends Object = Object>(
        obj: T,
        excludeNull: boolean = true,
        excludeUndefined: boolean = true)
        : {
        [p: string]: any
    } {
        return Object.fromEntries(Object.entries(obj).filter(([_, val]) =>
            ((excludeNull && val !== null) || !excludeNull) &&
            ((excludeUndefined && typeof val !== 'undefined') || !excludeUndefined)
        ))
    }

    /**
     * Converts string value to boolean
     * @param string
     * @return boolean
     */
    public static tryConvertStringToBoolean(string: string): boolean | undefined {
        const _true = ["true", "yes", "True"];
        const _false = ["false", "no", "False"];
        let s1 = string?.trim().toLowerCase().replaceAll(/[^a-zA-Z0-9]/g, '');
        if (_true.includes(s1))
            return true;
        else if (_false.includes(s1))
            return false;
        else return undefined;
    }

    /**
     * Returns a 0–1 similarity score for two strings (higher is a closer match).
     * @param {string} s1
     * @param {string} s2
     */
    public static getStringSimilarityPercentage(s1: string, s2: string): number {
        return Utils.similarity(s1, s2);
    }

    /**
     * Finds the best matching string from the given list based on the given item
     * @param {string[]} list
     * @param {string} item
     */
    public static findBestMatchingString(list: string[], item: string): string | undefined {
        const valueAndPercentageOfSimilarity = list.map(e => ({
            value: e,
            similarityPercentage: Utils.getStringSimilarityPercentage(e, item),
        }));
        const bestMatching = valueAndPercentageOfSimilarity
            .reduce((previousValue, currentValue) => {
                if (currentValue.similarityPercentage > (previousValue?.similarityPercentage ?? 0)) return currentValue;
                else return previousValue;
            }, {value: undefined, similarityPercentage: 0} as any);
        return bestMatching?.value ?? undefined;
    }

    /**
     * Calculates the matching percentage of two strings using the [calculateSimilarity] callback
     * @param s1
     * @param s2
     * @private
     */
    private static similarity(s1: string, s2: string): number {
        let longer = s1?.replaceAll(/[^a-zA-Z0-9]/g, '') ?? undefined;
        let shorter = s2?.replaceAll(/[^a-zA-Z0-9]/g, '') ?? undefined;
        if (s1.length < s2.length) {
            longer = s2;
            shorter = s1;
        }
        let longerLength = longer.length;
        if (longerLength === 0) {
            return 1.0;
        }
        return (longerLength - Utils.calculateSimilarity(longer, shorter)) / parseFloat(String(longerLength));
    }

    /**
     * Gets the percentage of similarity between two strings:
     * Src: https://stackoverflow.com/questions/10473745/compare-strings-javascript-return-of-likely
     * Based on Levenshtein distance algorithm
     * @param string1
     * @param string2
     * @private
     */
    private static calculateSimilarity(string1: string, string2: string): number {
        let s1 = string1.toLowerCase();
        let s2 = string2.toLowerCase();

        let costs: any[] = [];
        for (let i = 0; i <= s1.length; ++i) {
            let lastValue = i;
            for (let j = 0; j <= s2.length; ++j) {
                if (i === 0)
                    costs[j] = j;
                else {
                    if (j > 0) {
                        let newValue = costs[j - 1];
                        if (s1.charAt(i - 1) !== s2.charAt(j - 1))
                            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
                        costs[j - 1] = lastValue;
                        lastValue = newValue;
                    }
                }
            }
            if (i > 0)
                costs[s2.length] = lastValue;
        }
        return costs[s2.length];
    }

    /**
     * Formats the card number by adding spaces after every 4 characters
     * @param input
     */
    public static formatCardNumber(input: string) {
        let counter = 1;
        let output = "";
        for (const char of input) {
            if (counter % 4 === 0 && counter !== input.length) {
                output = output.concat(`${char + "\xa0\xa0"}`);
            } else {
                output = output.concat(char);
            }
            ++counter
        }
        return output;
    }

    /**
     * Flattens an object. if the parent key exists, then prepends the parent key with the key as it constructs the obejct
     * @param object {any}
     * @param parentKey {string | null}
     * @private
     */
    private static flatten(object: any, parentKey: string | null = null): any {
        return [].concat(
            ...Object.keys(object).map((key) => typeof object[key] === 'object'
                ? this.flatten(object[key], parentKey ? `${parentKey}-${key}` : key)
                : ((parentKey) ? {[`${parentKey}-${key}`]: object[key]} : {[key]: object[key]})
            )
        );
    }

    /**
     * Creates a new flattened object off of the given object
     * @param object {any}
     */
    private static flattenObject(object: any) {
        return Object.assign({}, ...this.flatten(object))
    }

    /**
     * Imputes the color theme values by cleaning the object values of any un-wanted string notations ('' or "")
     * @param obj
     */
    private static deepImputeColorTheme(obj: any): any {
        if (typeof obj === 'object' && obj !== null) {
            for (const [key, objElement] of Object.entries(obj)) {
                obj[key] = this.deepImputeColorTheme(objElement);
            }
            return obj;
        }
        if (typeof obj === 'string') {
            return obj
                .replaceAll('`', '')
                .replaceAll('"', '')
                .replaceAll('\\', '')
        }
        return obj;
    }

    /**
     * Imputes the provided color theme object by stripping the string values of any inner objects of any unwanted characters.
     * @param colorTheme
     */
    public static imputeColorTheme(colorTheme: any): Record<string, any> {
        if (colorTheme === null || typeof colorTheme !== 'object') {
            return DefaultColorTheme;
        }
        return this.deepImputeColorTheme(colorTheme)
    }

    /**
     * Prompts the user to select a file from the local machine.
     * @param acceptedTypes     the types of the files that are accepted in the prompted dialog.
     */
    static async promptFileSelection(...acceptedTypes: string[]): Promise<File | null> {
        return new Promise((resolve) => {
            if (!acceptedTypes.length) {
                return resolve(null);
            }
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = acceptedTypes.join();
            // @ts-ignore
            input.onchange = (e: ChangeEvent<HTMLInputElement>) => {
                if (!e.target?.files?.length)
                    return resolve(null);
                for (const file of e.target.files) {
                    if (accepts(file, acceptedTypes)) {
                        return resolve(e.target.files.item(0));
                    }
                }
                return resolve(null);
            }
            input.style.display = 'none';

            const button = document.createElement('button');
            button.onclick = (e) => input.click()
            input.style.display = 'none';
            document.body.appendChild(input);
            document.body.appendChild(button);
            button.click();
            document.body.removeChild(input);
            document.body.removeChild(button);
        })
    }

    /**
     * Formats the bytes into a human-readable format.
     * @param bytes        The bytes to format.
     * @returns            The formatted bytes.
     */
    static formatBytes(bytes: number): string {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Reorders the entities based on the new order index of the entity to move.
     *
     * @param list              The list of entities to reorder.
     * @param oldIndex          The old index of the entity to move.
     * @param newIndex          The new index of the entity to move.
     * @param orderIndexKey     The key of the order index property of the entity.
     */
    static reorderList<T extends object>(list: T[], oldIndex: number, newIndex: number, orderIndexKey?: keyof T): T[] {
        let entities = Array.from(list);

        if (oldIndex < 0 || oldIndex >= entities.length || newIndex < 0 || newIndex >= entities.length) {
            throw new Error("Invalid indices");
        }

        const [movedElement] = entities.splice(oldIndex, 1); // Remove the element from the old index
        entities.splice(newIndex, 0, movedElement); // Insert the element at the new index

        if (orderIndexKey) {
            entities = entities.map((entity, index) => {
                entity[orderIndexKey] = index as T[keyof T]
                return entity;
            });
        }

        return entities;
    }

    /**
     * Does nothing.
     */
    static noop() {
        return void 0;
    }

    /**
     * Converts a string to a slug.
     * @param decimal
     */
    static decimalToFraction(decimal: number): string | undefined {
        function greatestCommonDivisor(a, b) {
            if (!b) {
                return a;
            }
            return greatestCommonDivisor(b, a % b);
        }

        if (decimal >= 1 || decimal <= 0) {
            return;
        }

        // Set initial denominator to 1
        let denominator = 1;

        // Multiply until decimal becomes an integer
        while (decimal % 1 !== 0) {
            decimal *= 10;
            denominator *= 10;
        }

        // Use the greatest common divisor to simplify the fraction
        const numerator = decimal;
        const gcd = greatestCommonDivisor(numerator, denominator);

        // Return the fraction as a string
        return `${numerator / gcd}/${denominator / gcd}`;
    }

    /**
     * Converts a given decimal number to a fraction string.
     * @param decimal
     */
    static technicalPropertyDecimalToFraction(decimal: number): string | undefined {
        const number = Number(decimal?.toString() ?? '');
        if (Number.isNaN(number)) {
            return;
        }
        const [beforeDecimal, afterDecimal] = number.toString().split('.');
        if (!afterDecimal || Number(afterDecimal) === 0) {
            return beforeDecimal;
        }
        const fraction = Utils.decimalToFraction(Number(`0.${afterDecimal}`));
        if (!fraction) {
            return beforeDecimal;
        }
        return `${beforeDecimal}-${fraction}`;
    }

    /**
     * Converts a given fraction string to a decimal number.
     * @param fraction
     */
    static technicalPropertyFractionToDecimal(fraction: string): number | undefined {
        if (!fraction) {
            return;
        }
        const [whole, fractionPart] = fraction.split('-');
        if (!fractionPart || !fractionPart.includes('/')) {
            return Number(whole);
        }
        const [numerator, denominator] = fractionPart.split('/');
        if (!numerator || !denominator) {
            return Number(whole);
        }
        return Number(whole) + (Number(numerator) / Number(denominator));
    }

    /**
     * Determines if technical property is valid.
     * @param fraction
     */
    static technicalPropertyIsValidFraction(fraction: string): boolean {
        const [whole, fractionPart] = fraction.split('-');
        if (!fractionPart) {
            return true;
        }
        const [numerator, denominator] = fractionPart.split('/');
        if (!numerator || !denominator) {
            return false;
        }
        return true;
    }

    /**
     * Validates and normalizes predefined industry property option content.
     * Uses the same Yup rules as product technical property predefined option entry.
     */
    static normalizeIndustryPropertyPredefinedOptionContent(
        typeId: number,
        content: string,
    ):
        | { valid: true; apiContent: string }
        | {
        valid: false;
        errorKind: 'required' | 'number' | 'decimal' | 'money' | 'fraction' | 'text';
        message: string
    } {
        const raw = (content ?? '').toString().trim();
        if (!raw.length) {
            return {valid: false, errorKind: 'required', message: ValidateMessages.required};
        }

        let titleSchema: any;
        let value: string | number = raw;
        let errorKind: 'number' | 'decimal' | 'money' | 'fraction' | 'text' = 'text';

        switch (typeId) {
            case ProductTechnicalPropertyTypeIds.number:
                errorKind = 'number';
                titleSchema = Yup.number()
                    .typeError(ValidateMessages.incorrectType('number'))
                    .integer(ValidateMessages.incorrectType('integer'))
                    .nullable()
                    .required(ValidateMessages.required);
                value = Number(value);
                break;
            case ProductTechnicalPropertyTypeIds.decimal:
                errorKind = 'decimal';
                titleSchema = Yup.number()
                    .typeError(ValidateMessages.incorrectType('decimal'))
                    .nullable()
                    .required(ValidateMessages.required);
                value = Number(value);
                break;
            case ProductTechnicalPropertyTypeIds.money:
                errorKind = 'money';
                titleSchema = Yup.number()
                    .typeError(ValidateMessages.incorrectType('currency'))
                    .min(0, 'Value must be non-negative')
                    .nullable()
                    .required(ValidateMessages.required);
                value = Number(value);
                break;
            case ProductTechnicalPropertyTypeIds.fraction:
                errorKind = 'fraction';
                titleSchema = Yup.string()
                    .fractionNumber(ValidateMessages.incorrectType('fraction'))
                    .nullable()
                    .required(ValidateMessages.required);
                break;
            case ProductTechnicalPropertyTypeIds.text:
            default:
                errorKind = 'text';
                titleSchema = Yup.string().trim().nullable().required(ValidateMessages.required);
                break;
        }

        const schema = Yup.object().shape({title: titleSchema});
        try {
            schema.validateSync({title: value}, {abortEarly: false});
        } catch (error: any) {
            return {
                valid: false,
                errorKind,
                message: error?.errors?.[0] ?? ValidateMessages.required,
            };
        }

        let apiContent = raw;
        if (typeId === ProductTechnicalPropertyTypeIds.fraction) {
            const decimal = Utils.technicalPropertyFractionToDecimal(raw);
            if (decimal === undefined || decimal === null || Number.isNaN(decimal)) {
                return {
                    valid: false,
                    errorKind: 'fraction',
                    message: ValidateMessages.incorrectType('fraction'),
                };
            }
            apiContent = String(decimal);
        }

        return {valid: true, apiContent};
    }

    /**
     * Formats the technical property value based on the type of the property.
     * @param typeId            The type ID of the technical property.
     * @param value             The value of the technical property.
     * @param nullReplacement   The replacement for when the value is null
     */
    static formatTechnicalPropertyValueDisplay(typeId: number, value: any, nullReplacement = undefined) {
        if (value === null || value === undefined) {
            return nullReplacement;
        }

        // if value is an stringified array, we parse the Array, and for each item in the array, we call the formatTechnicalPropertyValueDisplay recursively and join them with a comma and a space.
        if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
            try {
                const arrayValue = JSON.parse(value);
                if (Array.isArray(arrayValue)) {
                    const parsedArray = arrayValue.map(e => this.formatTechnicalPropertyValueDisplay(typeId, e, nullReplacement)).filter(Boolean);
                    if (parsedArray.length > 0) {
                        return parsedArray.join(', ');
                    }
                    return nullReplacement;
                }
            } catch (e) {
                // not a valid JSON, continue with the normal flow
            }
        }

        switch (typeId) {
            case ProductTechnicalPropertyTypeIds.number:
            case ProductTechnicalPropertyTypeIds.decimal: {
                if (Number.isNaN(Number(value)))
                    return nullReplacement;
                return Number(value);
            }
            case ProductTechnicalPropertyTypeIds.date:
                return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
            case ProductTechnicalPropertyTypeIds.boolean:
                if (typeof value === 'boolean') {
                    return value ? 'Yes' : 'No';
                }
                return value?.toString()?.toLowerCase() === 'true' ? 'Yes' : 'No';
            case ProductTechnicalPropertyTypeIds.money:
                return formatMoney(value);
            case ProductTechnicalPropertyTypeIds.fraction:
                return Utils.technicalPropertyDecimalToFraction(value);
            case ProductTechnicalPropertyTypeIds.text:
                return value;
            case ProductTechnicalPropertyTypeIds.color:
                return (
                    <ColorInputColorVisualizer
                        disablePopover={true}
                        bgColor={value}
                        isBgColorValid={!!value}
                        aria-readonly={'true'}
                        sx={{maxWidth: 30, minWidth: 30, height: 30, pointerEvents: 'none'}}
                    />
                )
            default:
                return value;
        }
    }

    /**
     * Resolves a variant property value for display by collecting all rows that share
     * the same industryPropertyId (multi-select) and joining them into one value.
     * @param typeId                The type ID of the technical property.
     * @param propertyEntries       The variant part's properties array.
     * @param industryPropertyId    The industry property id to resolve.
     * @param nullReplacement       The replacement for when a value is null.
     * @returns {{raw: *, formatted: *}}
     */
    static resolveVariantPropertyDisplay(typeId: number, propertyEntries: any[], industryPropertyId: any, nullReplacement = undefined) {
        const matching = (propertyEntries ?? []).filter(e => e.industryPropertyId === industryPropertyId);
        if (!matching.length) {
            return {raw: nullReplacement ?? null, formatted: nullReplacement};
        }

        const rawParts: any[] = [];
        const formattedParts: any[] = [];
        for (const entry of matching) {
            const rawSegment = (entry.value === null || entry.value === undefined || entry.value === '')
                ? nullReplacement
                : entry.value;
            const formattedSegment = this.formatTechnicalPropertyValueDisplay(typeId, entry.value, nullReplacement);

            if (rawSegment !== null && rawSegment !== undefined && rawSegment !== '') {
                rawParts.push(rawSegment);
            }
            if (formattedSegment !== null && formattedSegment !== undefined && formattedSegment !== '') {
                formattedParts.push(formattedSegment);
            }
        }

        if (!formattedParts.length) {
            return {raw: nullReplacement ?? null, formatted: nullReplacement};
        }

        const joinRaw = (parts) => parts.length === 1 ? parts[0] : parts.join(', ');
        const joinFormatted = (parts) => {
            if (parts.length === 1) {
                return parts[0];
            }
            const allPrimitive = parts.every(p => p === null || ['string', 'number', 'boolean'].includes(typeof p));
            if (allPrimitive) {
                return parts.join(', ');
            }
            // Non-primitive formatted values (e.g. color visualizers) cannot be string-joined
            return parts.flatMap((part, i) => i === 0 ? [part] : [', ', part]);
        };
        return {
            raw: rawParts.length ? joinRaw(rawParts) : (nullReplacement ?? null),
            formatted: joinFormatted(formattedParts),
        };
    }

    /**
     * Formats the technical property value based on the type of the property.
     * @param typeId        The type ID of the technical property.
     * @param value         The value of the technical property.
     */
    static parseTechnicalPropertyValue(typeId: number, value: any) {
        switch (typeId) {
            case ProductTechnicalPropertyTypeIds.number:
            case ProductTechnicalPropertyTypeIds.decimal:
            case ProductTechnicalPropertyTypeIds.money:
                return Number(value);
            case ProductTechnicalPropertyTypeIds.date:
                return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
            case ProductTechnicalPropertyTypeIds.boolean:
                if (typeof value === 'boolean') {
                    return value;
                }
                return value === 'true';
            case ProductTechnicalPropertyTypeIds.fraction:
                return Utils.technicalPropertyFractionToDecimal(value);
            case ProductTechnicalPropertyTypeIds.text:
            default:
                return value;
        }
    }

    /**
     * Fetches the OS of the system with 90% accuracy.
     *
     * @author Vladyslav Turak
     * @see https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
     */
    private static getOS() {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator.platform;
        const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
        const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
        const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
        let os: any = null;

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.mac;
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.ios;
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = OperatingSystems.windows;
        } else if (/Android/.test(userAgent)) {
            os = OperatingSystems.android;
        } else if (/Linux/.test(platform)) {
            os = OperatingSystems.linux;
        }
        return os;
    }

    /**
     * Fetches the information about the os of the user.
     */
    static getOsSpecs() {
        const os = this.getOS();
        const isMac = os === OperatingSystems.mac;
        const isIOS = os === OperatingSystems.ios;
        const isWindows = os === OperatingSystems.windows;
        const isAndroid = os === OperatingSystems.android;
        const isLinux = os === OperatingSystems.linux;
        return {
            os,
            isMac,
            isIOS,
            isWindows,
            isAndroid,
            isLinux,
        };
    }

    /**
     * Determines if the element has scrolled into the view with respect to the window object.
     * @param el
     * @param partial
     */
    static isScrolledIntoView(el: HTMLElement, partial = false): boolean {
        if (!el) return false;
        const firstScrollingContainer = getFirstScrollingAncestor(el);
        const rect = el.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom;
        const containerInnerHeight = firstScrollingContainer.scrollHeight;
        // Only completely visible elements return true:
        // Partially visible elements return true:
        if (partial) {
            return elemTop < containerInnerHeight && elemBottom >= 0;
        }
        return (elemTop >= 0) && (elemBottom <= containerInnerHeight);
    }

    /**
     * Copies the given text to the clipboard.
     *
     * @param text    the text to be copied to the clipboard
     * @return        true if the text was copied successfully, false otherwise
     */
    static copyToClipboard(text: string): Promise<boolean> {
        return new Promise((res) => {
            if (!navigator.clipboard) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = text;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                    res(true)
                } catch (err) {
                    console.error('Failed to copy text: ', err);
                    res(false)
                }
                document.body.removeChild(textArea);
            } else {
                navigator.clipboard.writeText(text)
                    .then(() => {
                        res(true)
                    })
                    .catch(err => {
                        console.error('Failed to copy text: ', err);
                        res(false)
                    });
            }
        })
    }

    /**
     * Fetches the unique entries from the given entries based on the fetcher function.
     * @param entries    The entries to fetch the unique entries from.
     * @param fetcher   The fetcher function to fetch the unique entries.
     */
    static getUniqueEntries(entries, fetcher) {
        return [...new Set(entries.map((e) => fetcher(e)))];
    }

    /**
     * Formats the given link url to be displayed.
     * @param url
     */
    static formatLinkUrlDisplay(url: string): string {
        if (!url) return '';
        url = url.replace('https://', '').replace('http://', '').replace('www.', '');
        return `www.${url}`;
    }

    /**
     * Encodes each path segment of a file URL so reserved characters (e.g. `#`, spaces)
     * in filenames are not treated as URL fragments or invalid path characters by the browser.
     * Passes through empty values, non-strings, and `data:` / `blob:` URLs unchanged.
     * @param url
     * @return {string | unknown}
     */
    static encodeFileUrl(url: unknown): unknown {
        if (!url || typeof url !== 'string')
            return url;
        if (/^(data:|blob:)/i.test(url))
            return url;

        const absoluteOriginMatch = url.match(/^(https?:\/\/[^/?#]+)/i);
        const origin = absoluteOriginMatch?.[1] ?? '';
        const remainder = origin ? url.slice(origin.length) : url;

        const queryIndex = remainder.indexOf('?');
        const pathname = queryIndex === -1 ? remainder : remainder.slice(0, queryIndex);
        const query = queryIndex === -1 ? '' : remainder.slice(queryIndex);

        const encodedPathname = pathname
            .split('/')
            .map((segment) => {
                if (!segment)
                    return segment;
                try {
                    return encodeURIComponent(decodeURIComponent(segment));
                } catch {
                    return encodeURIComponent(segment);
                }
            })
            .join('/');

        return origin + encodedPathname + query;
    }

    /**
     * Sanitizes a filename for API upload by replacing characters that break browser fetching
     * (spaces, `#`, `?`, `&`, `%`, quotes, path separators, etc.) with `-`, collapsing repeats,
     * and trimming leading/trailing dashes while preserving the file extension.
     * @param fileName
     * @return {string}
     */
    static sanitizeFileName(fileName: string): string {
        if (!fileName || typeof fileName !== 'string')
            return fileName ?? '';

        const lastDotIndex = fileName.lastIndexOf('.');
        const hasExtension = lastDotIndex > 0 && lastDotIndex < fileName.length - 1;
        const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
        const extension = hasExtension ? fileName.slice(lastDotIndex) : '';

        const sanitizedBase = baseName
            .replace(/[\s#?&%"'<>|*:/\\]+/g, '-')
            .replace(/[\x00-\x1f\x7f]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');

        return `${sanitizedBase || 'file'}${extension}`;
    }

    /**
     * Downloads the given blob with the given exported file name.
     * @param blob                  the blob to be downloaded
     * @param exportedFileName      the name of the exported file
     */
    static downloadFile(blob: Blob, exportedFileName: string): void {
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, exportedFileName);
        } else {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = exportedFileName;
            link.style.visibility = 'hidden';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        }
    }

    /**
     * Exports a .json file from the provided [json] object and the [fileTitle].
     * @param json              the json object to be exported
     * @param fileTitle         the title of the file
     * @param extension        the extension of the file
     */
    static exportJsonFile(json: Record<string, any>, {fileTitle, extension = '.json',}: {
        fileTitle?: string,
        extension?: string
    } = {}): void {
        const exportedFileName = fileTitle ? fileTitle.toLowerCase().endsWith(extension) ? fileTitle : `${fileTitle}${extension}` : `export${extension}`;
        const text = JSON.stringify(json, null, 4);
        const blob = new Blob([text], {type: 'data:application/json;charset=utf-8;'});
        this.downloadFile(blob, exportedFileName);
    }

    /**
     * Exports a .csv file from the provided [data] and the [fileTitle].
     * @param data              the data to be exported
     * @param fileTitle         the title of the file
     * @param options           the options for the csv export
     * @param extension        the extension of the file
     */
    static exportCsvFile(data: any, {fileTitle, options, extension = '.csv'}: {
        fileTitle?: string,
        options?: UnparseConfig,
        extension?: string
    } = {}): void {
        const csv = jsonToCSV(data, {skipEmptyLines: true, ...(options ?? {})});
        const exportedFileName = fileTitle ? fileTitle.toLowerCase().endsWith(extension) ? fileTitle : `${fileTitle}${extension}` : `export${extension}`;
        const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
        this.downloadFile(blob, exportedFileName);
    }

    /**
     * Exports a .bizimport file from the provided [text] and the [fileTitle].
     * @param text              the file content to be exported
     * @param fileTitle         the title of the file
     * @param extension        the extension of the file
     */
    static async exportBizImportFile(text: string, {fileTitle, extension = '.bizimport',}: {
        fileTitle?: string,
        extension?: string
    } = {}): Promise<void> {
        const encryptedData = await EncryptionUtils.encrypt(text);
        const exportedFileName = fileTitle ? fileTitle.toLowerCase().endsWith(extension) ? fileTitle : `${fileTitle}${extension}` : `export${extension}`;
        const blob = new Blob([encryptedData], {type: 'data:application/json;charset=utf-8;'});
        this.downloadFile(blob, exportedFileName);
    }

    /**
     * Imports a .bizimport file and decrypts it.
     * @param file             the file to be imported
     * @return                  the decrypted content of the file
     */
    static async importBizImportFile(file: File): Promise<string | undefined> {
        try {
            const text = await readFile(file, ReadingFileAs.text) as string;
            return await EncryptionUtils.decrypt(text);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Takes a screenshot of the users' browser window.
     *
     * * first uses the mediaDevices of the navigator to take the screenshot, if erroneous, creates the screenshot
     * using the canvas approach.
     * @param allowExplicitScreenshot       if true, allows the user to take a screenshot explicitly
     * @return                              the base64 encoded string of the screenshot
     */
    static async screenshotUserBrowserWindow(allowExplicitScreenshot = false): Promise<string | null> {
        // Skip canvas path inside Flutter WebView
        if (this.isFlutterWebView()) {
            return null;
        }
        try {
            if (!allowExplicitScreenshot) {
                // throw so that the screenshot is handled with a canvas
                // noinspection ExceptionCaughtLocallyJS
                throw Error("");
            }
            // media display with user permission screenshot.
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            const video = document.createElement("video");
            if (!context) {
                // throw so that the screenshot is handled with a canvas
                // noinspection ExceptionCaughtLocallyJS
                throw Error("");
            }

            const captureStream = await navigator.mediaDevices?.getDisplayMedia({video: false});
            video.srcObject = captureStream;
            context.drawImage(video, 0, 0, window.innerWidth, window.innerHeight);
            captureStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            return this.extractCanvasContentBytes(canvas);
        } catch (err) {
            // canvas screenshot
            const canvas = await html2canvas(document.body);
            return this.extractCanvasContentBytes(canvas);
        }
    }

    /**
     * Creates the content bytes of the image obtained from the given canvas.
     * @param canvas                the canvas to be converted to content bytes
     * @return                      the base64 encoded string of the image
     */
    static async extractCanvasContentBytes(canvas: HTMLCanvasElement): Promise<string | null> {
        const toBlob = (): Promise<Blob | null> => new Promise(r => canvas.toBlob(r));
        const blob = await toBlob();
        if (!blob) {
            return null;
        }
        const binaryString = await this.readFile(blob, FileReadingOptions.binaryString)
        if (!binaryString) {
            return null;
        }
        return btoa(binaryString);
    }

    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.arrayBuffer): PromiseLike<ArrayBuffer | null>;
    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.json): PromiseLike<Record<string, any> | null>;
    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions.text | FileReadingOptions.dataUrl | FileReadingOptions.binaryString): PromiseLike<string | null>;
    /**
     * Reads the given file based on the provided method of reading.
     * @param file          the file to be read
     * @param as            the method of reading the file
     * @return              the result of the reading
     */
    static readFile(file: File | Blob, as: FileReadingOptions): PromiseLike<string | ArrayBuffer | Record<string, any> | null> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            switch (as) {
                case FileReadingOptions.text:
                case FileReadingOptions.json:
                    reader.readAsText(file);
                    break;
                case FileReadingOptions.dataUrl:
                    reader.readAsDataURL(file);
                    break;
                case FileReadingOptions.arrayBuffer:
                    reader.readAsArrayBuffer(file);
                    break;
                case FileReadingOptions.binaryString:
                    reader.readAsBinaryString(file);
                    break;
            }
            reader.onload = () => {
                if (as === FileReadingOptions.json) {
                    try {
                        resolve(JSON.parse(reader.result as string))
                    } catch (e) {
                        reject(e);
                    }
                } else {
                    resolve(reader.result)
                }
            };
            reader.onerror = error => reject(error);
        });
    }

    /**
     * Detects if we are running inside the Flutter WebView host app.
     * Uses explicit signals first (JS channel + UA token), then light heuristics.
     */
    static isFlutterWebView(): boolean {
        const w = window as any;
        const ua = navigator.userAgent || "";

        // Explicit signals we control
        const hasAndroidChannel = !!w.FlutterBridge?.postMessage;
        const hasIOSHandler = !!w.webkit?.messageHandlers?.FlutterBridge;
        const hasCustomUAToken = /\bOperatorApplication Version 2\.0\.0\b/i.test(ua);

        // Light heuristics as a fallback ( dkept last, and weak)
        const androidWV = /\bwv\b/i.test(ua); // Android WebView often includes ; wv
        const iosWK = /\bAppleWebKit\b/i.test(ua) && !/\bSafari\b/i.test(ua); // WKWebView often lacks 'Safari'

        return hasAndroidChannel || hasIOSHandler || hasCustomUAToken || androidWV || iosWK;
    }

    /**
     * Rounds a number to the specified precision.
     * @param value
     * @param precision
     */
    public static roundNumberWithDecimalPoints(value: number, precision: number = 1): number {
        if (isNaN(value)) {
            return 0;
        }
        if (precision < 0) {
            precision = 0;
        }
        if (value % 1 === 0) {
            return value; // no decimal places, return as is
        }
        return value.toFixed(precision) === '0' ? 0 : parseFloat(value.toFixed(precision));
    }

    /**
     * Determines whether to skip the special character error based on the given ID.
     * @param id
     */
    static skipSpecialCharacterError(id: string | undefined) {
        return PartnerIndustryRules.allowsKeywordSpecialCharacters(id);
    }

    /**
     * Whether the given industry supports VIN decoder (by id).
     */
    static isVinDecoderIndustry(industry?: { id?: string | number; } | null): boolean {
        return PartnerIndustryRules.isVinDecoderIndustry(industry);
    }

    /**
     * Unicode for Windows-1252 bytes 0x80-0x9F.
     * Bytes 0x81, 0x8D, 0x8F, 0x90, and 0x9D are undefined and become U+FFFD.
     */
    private static readonly windows1252C1 = [
        '\u20AC', '\uFFFD', '\u201A', '\u0192', '\u201E', '\u2026', '\u2020', '\u2021',
        '\u02C6', '\u2030', '\u0160', '\u2039', '\u0152', '\uFFFD', '\u017D', '\uFFFD',
        '\uFFFD', '\u2018', '\u2019', '\u201C', '\u201D', '\u2022', '\u2013', '\u2014',
        '\u02DC', '\u2122', '\u0161', '\u203A', '\u0153', '\uFFFD', '\u017E', '\u0178',
    ];

    /**
     * Maps one Windows-1252 byte to its Unicode character.
     * Bytes 0x00-0x7F and 0xA0-0xFF use the byte value as the code point.
     * @param byte
     */
    private static characterFromWindows1252Byte(byte: number): string {
        if (byte <= 0x7F || byte >= 0xA0) {
            return String.fromCharCode(byte);
        }
        return Utils.windows1252C1[byte - 0x80];
    }

    /**
     * Rewrites Windows-1252 bytes that were stored as the control characters U+0080-U+009F.
     * @param text
     */
    static repairWindows1252Controls(text: string): string {
        if (!text) {
            return '';
        }
        return text.replace(/[\u0080-\u009F]/g, (char) => Utils.characterFromWindows1252Byte(char.charCodeAt(0)));
    }

    /**
     * Decodes spreadsheet bytes as UTF-8.
     * When the bytes are not valid UTF-8, decodes them with the Windows-1252 map.
     * Either result then repairs leftover U+0080-U+009F controls.
     * @param bytes
     */
    static decodeImportedText(bytes: Uint8Array): string {
        try {
            const utf8 = new TextDecoder('utf-8', {fatal: true}).decode(bytes);
            return Utils.repairWindows1252Controls(utf8);
        } catch {
            let decoded = '';
            for (let index = 0; index < bytes.length; index++) {
                decoded += Utils.characterFromWindows1252Byte(bytes[index]);
            }
            return Utils.repairWindows1252Controls(decoded);
        }
    }

    /**
     * Trims a part code and folds dash-like characters to an ASCII hyphen.
     * Spaces in the middle of the code are left as typed.
     * @param value
     */
    static normalizePartCode(value: unknown): string {
        if (value == null) {
            return '';
        }
        return String(value)
            .replace(/[\u00AD\u200B-\u200D\uFEFF]/g, '')
            .replace(/[\u0096\u0097\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFF0D\uFFFD]/g, '-')
            .trim();
    }
}

export default Utils;
